// ─────────────────────────────────────────────────────────────
// CategoriesView — CRUD for song categories
// ─────────────────────────────────────────────────────────────

import {createElement} from "@opendaw/lib-jsx"
import {Lifecycle} from "@opendaw/lib-std"
import {STApi} from "../STApi"
import {STCategory} from "../STTypes"
import {confirmDelete} from "./ConfirmDeleteDialog"

const DEFAULT_COLORS = [
    "#7c5cbf", "#5cb8bf", "#bf5c7c", "#80c080",
    "#c0b060", "#6090c0", "#c08050", "#9060c0",
]

export const CategoriesView = ({}: {lifecycle: Lifecycle}) => {
    let categories: STCategory[] = STApi.getCategories()
    let editingId: string | null = null
    let usageCounts: Record<string, number> = {}

    const listEl: HTMLElement = <div/>
    const nameInput: HTMLInputElement = (
        <input className="st-input" type="text" placeholder="Category name…" style={{flex:"1"}}/>
    )
    const colorInput: HTMLInputElement = (
        <input type="color" value="#7c5cbf" style={{
            width:"28px", height:"28px", padding:"2px",
            background:"none", border:"1px solid rgba(255,255,255,0.1)",
            borderRadius:"4px", cursor:"pointer"
        }}/>
    )
    const addBtn: HTMLElement = (
        <div className="st-btn primary" style={{flexShrink:"0"}}>Save</div>
    )
    const cancelBtn: HTMLElement = (
        <div className="st-btn" style={{flexShrink:"0", display:"none"}}>Cancel</div>
    )
    const nameError: HTMLElement = (
        <div style={{
            display:"none", fontSize:"0.6rem", color:"var(--color-red)",
            marginTop:"0.25rem", marginBottom:"0.2rem",
        }}>A category with this name already exists.</div>
    )

    const resetForm = () => {
        editingId = null
        nameInput.value = ""
        colorInput.value = "#7c5cbf"
        addBtn.textContent = "Add"
        cancelBtn.style.display = "none"
    }

    const renderList = () => {
        listEl.innerHTML = ""
        if (categories.length === 0) {
            listEl.appendChild(
                <div className="st-empty">No categories yet.<br/>Add one below.</div>
            )
            return
        }
        for (const cat of categories) {
            const count = usageCounts[cat.id] ?? 0
            const countBadge: HTMLElement = (
                <span style={{
                    fontSize:"9px", fontWeight:"600", letterSpacing:"0.04em",
                    padding:"1px 5px", borderRadius:"3px", flexShrink:"0",
                    background:"rgba(255,255,255,0.07)", color:"rgba(255,255,255,0.35)",
                }}>{count} {count === 1 ? "song" : "songs"}</span>
            )
            const item: HTMLElement = (
                <div className="st-crud-item">
                    <div className="st-color-dot" style={{backgroundColor: cat.color}}/>
                    <span className="st-crud-name">{cat.name}</span>
                    {countBadge}
                    <div className="st-crud-actions">
                        <div className="st-btn" data-id={cat.id} data-action="edit" style={{padding:"0.2rem 0.5rem"}}>Edit</div>
                        <div className="st-btn danger" data-id={cat.id} data-action="del" style={{padding:"0.2rem 0.5rem"}}>✕</div>
                    </div>
                </div>
            )
            listEl.appendChild(item)
        }
        listEl.querySelectorAll<HTMLElement>("[data-action='edit']").forEach(btn => {
            btn.addEventListener("click", () => {
                const id = btn.getAttribute("data-id")!
                const cat = categories.find(c => c.id === id)
                if (!cat) return
                editingId = id
                nameInput.value = cat.name
                colorInput.value = cat.color
                addBtn.textContent = "Save"
                cancelBtn.style.display = "inline-flex"
                nameInput.focus()
            })
        })
        listEl.querySelectorAll<HTMLElement>("[data-action='del']").forEach(btn => {
            btn.addEventListener("click", async () => {
                const id = btn.getAttribute("data-id")!
                const cat = categories.find(c => c.id === id)
                if (!cat) return
                const confirmed = await confirmDelete({name: cat.name, kind: "category"})
                if (!confirmed) return
                await STApi.deleteCategory(id)
                categories = STApi.getCategories()
                if (editingId === id) resetForm()
                renderList()
            })
        })
    }

    addBtn.addEventListener("click", async () => {
        const name = nameInput.value.trim()
        if (!name) return
        // Duplicate name guard — case-insensitive, exclude self when editing
        const duplicate = STApi.getCategories().find(c =>
            c.name.toLowerCase() === name.toLowerCase() && c.id !== editingId
        )
        if (duplicate) {
            nameError.style.display = "block"
            nameInput.focus()
            return
        }
        nameError.style.display = "none"
        let savedCat: STCategory
        if (editingId) {
            STApi.updateCategory(editingId, {name, color: colorInput.value})
            savedCat = STApi.getCategories().find(c => c.id === editingId)!
        } else {
            savedCat = STApi.createCategory(name, colorInput.value)
        }
        // Push to D1 immediately — no publish needed for category changes
        try { await STApi.upsertCategory(savedCat) }
        catch (e) { console.warn("[CategoriesView] upsertCategory failed:", e) }
        categories = STApi.getCategories()
        resetForm()
        renderList()
    })
    nameInput.addEventListener("input", () => { nameError.style.display = "none" })

    cancelBtn.addEventListener("click", resetForm)

    // Quick color presets
    const colorPresets: HTMLElement = (
        <div style={{display:"flex", gap:"0.3rem", flexWrap:"wrap", marginBottom:"0.5rem"}}>
            {DEFAULT_COLORS.map(c => {
                const dot: HTMLElement = (
                    <div style={{
                        width:"16px", height:"16px", borderRadius:"50%",
                        backgroundColor:c, cursor:"pointer",
                        border:"2px solid transparent",
                    }}/>
                )
                dot.addEventListener("click", () => { colorInput.value = c })
                return dot
            })}
        </div>
    )

    // Fetch usage counts on mount; re-render when counts arrive
    STApi.getUsageCounts()
        .then(counts => { usageCounts = counts.categories; renderList() })
        .catch(() => { /* non-fatal — counts stay 0 */ })

    renderList()

    return (
        <div>
            {listEl}
            <div className="st-section" style={{borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                <div className="st-label" style={{marginBottom:"0.5rem"}}>Add / Edit Category</div>
                {colorPresets}
                <div className="st-row" style={{marginBottom:"0.1rem"}}>
                    {colorInput}
                    {nameInput}
                </div>
                {nameError}
                <div className="st-row">
                    {addBtn}
                    {cancelBtn}
                </div>
            </div>
        </div>
    )
}
